import { DateTime } from "luxon";
import { z } from "zod";

import type { AthenaCoverageWriteConfig, AthenaCoverageWriteReviewInput } from "@/configuration/ehr/athena/types";
import { formatTprCasePolicyMemberId, resolveTprCasePolicyPackageId } from "@/configuration/groups/tpr/case-policies";
import {
  findUniqueTprInsurancePackageNumber,
  isTprUnitedHealthcarePayer,
  resolveTprEligibilityCustomerPayer,
} from "@/configuration/groups/tpr/workflows/insurance-grid";
import { normalizeInsuranceMemberId } from "@/packages/shared/src/models/insurance-evidence";
import payers from "@/src/data/payers.json";
import {
  eligibilityRequestMemberId,
  eligibilitySubscriberFieldsFromCoverage,
} from "@/src/lib/blocks/eligibility-check-inputs";
import { athenaStandardCoverageRank } from "@/src/lib/ehr/athena/athena-coverage-sequence";
import { resolveConfirmedAthenaInsurancePlan } from "@/src/lib/ehr/athena/athena-insurance-plan-catalog";
import { isSameDayEligibilityCheck } from "@/src/lib/insurance/eligibility-freshness";
import { CoverageType, type Coverage } from "@/src/models/coverage";

export function normalizeTprMemberIdForAthenaWrite(coverage: Coverage, memberId: string): string {
  if (isTprUnitedHealthcarePayer(coverage)) {
    const normalizedMemberId = memberId.replace(/[\s-]/g, "");

    return /^\d{11}$/.test(normalizedMemberId) ? normalizedMemberId.slice(0, 9) : memberId.replace(/-\d{2}$/, "");
  }

  if (coverage.customerPayer === "44273") {
    return memberId.replace(/^(HH[A-Z0-9]{7})-?\d{2}$/i, "$1");
  }

  const payerText = [coverage.payer, coverage.plan, coverage.customerPayer].filter(Boolean).join(" ");

  if (/\boscar\b/i.test(payerText)) {
    return memberId.replace(/-/g, "");
  }

  const casePolicyMemberId = formatTprCasePolicyMemberId(coverage, memberId);

  if (casePolicyMemberId !== null) return casePolicyMemberId;

  return /\bcigna\b/i.test(payerText) && /^\d{9}$/.test(memberId) ? `${memberId}01` : memberId;
}

export function tprWrittenMemberId(coverage: Coverage): string | null {
  return normalizeInsuranceMemberId(normalizeTprMemberIdForAthenaWrite(coverage, coverage.subscriberId?.trim() ?? ""));
}

const eligibilityPersonSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  dateOfBirth: z.string().regex(/^\d{8}$/),
});
const eligibilityRequestSchema = z.object({
  tradingPartnerServiceId: z.string().min(1),
  subscriber: eligibilityPersonSchema.extend({ memberId: z.string().trim().min(1) }),
  dependents: z.array(eligibilityPersonSchema).max(1).optional(),
  encounter: z.object({
    serviceTypeCodes: z.array(z.string()),
    dateOfService: z.string().optional(),
    beginningDateOfService: z.string().optional(),
    endDateOfService: z.string().optional(),
  }),
});

export function reviewTprCoverageWrite(input: AthenaCoverageWriteReviewInput): string | null {
  const { coverage, insurancePackageId, casePolicy, existing, eligibility, confirmedPlans, patient, group, now } =
    input;
  const writtenMemberId = tprWrittenMemberId(coverage);
  const active = existing.filter((policy) => !policy.ehrCancelledAt);
  const matches = active.filter(
    (policy) =>
      policy.casePolicy === casePolicy &&
      policy.insurancePackageId === insurancePackageId &&
      normalizeInsuranceMemberId(policy.subscriberId) === writtenMemberId
  );

  const [match] = matches;
  const isSecondary = Number.isInteger(coverage.order) && (coverage.order ?? 0) > 1;
  const canReuseSecondary =
    isSecondary &&
    matches.length === 1 &&
    active.filter((policy) => policy.insurancePackageId === insurancePackageId).length === 1;

  if (matches.length === 1 && match && (casePolicy || athenaStandardCoverageRank(existing, match) === coverage.order)) {
    return null;
  }

  if (
    existing.some(
      (policy) =>
        policy.ehrCancelledAt &&
        policy.insurancePackageId === insurancePackageId &&
        normalizeInsuranceMemberId(policy.subscriberId) === writtenMemberId
    )
  ) {
    return "This member's policy was previously cancelled in Athena. Review that correction before adding it again, regardless of who cancelled it.";
  }

  // An exact existing secondary may move behind a new primary. Corrections to
  // its package/member or case-policy ordering still need review.
  if (
    active.some(
      (policy) => policy.casePolicy || (policy.insurancePackageId === insurancePackageId && !canReuseSecondary)
    )
  ) {
    return "The proposed insurance would correct an existing Athena package or change case-policy ordering. Verify the chart manually before continuing.";
  }

  if (casePolicy) {
    if (
      coverage.coverageType !== CoverageType.INSURANCE ||
      !writtenMemberId ||
      resolveTprCasePolicyPackageId(coverage)?.toString() !== insurancePackageId
    ) {
      return "A configured case-policy package and member ID are required before adding insurance.";
    }

    return null;
  }

  if (coverage.coverageType !== CoverageType.INSURANCE) {
    return "Confirm Self Pay directly in Athena before continuing; electronic eligibility does not authorize this write.";
  }

  if (isSecondary) {
    if (!writtenMemberId) {
      return "A member ID is required before adding secondary insurance.";
    }

    if (matches.length === 1) return null;

    // The referral eligibility result belongs to the primary. Resolve the
    // secondary from its own identity without borrowing that result.
    const catalogPackageId = resolveConfirmedAthenaInsurancePlan(confirmedPlans, coverage, null, null, null)?.packageId;
    const packageId = coverage.valerieInsurancePlanId
      ? catalogPackageId
      : (findUniqueTprInsurancePackageNumber({
          payer: coverage.payer,
          plan: coverage.plan,
          groupNumber: coverage.groupNumber,
          eligibilityProductText: null,
        }) ?? catalogPackageId);

    return packageId === insurancePackageId
      ? null
      : "The secondary insurance needs a confirmed Athena package match. Confirm the specific plan and network before adding coverage.";
  }

  if (coverage.order !== 1) {
    return "Confirm the insurance order before adding coverage.";
  }

  const currentTime = DateTime.fromISO(now, { zone: group.timezone });

  if (
    !eligibility ||
    eligibility.deletedAt ||
    eligibility.patientId !== patient.id ||
    eligibility.groupId !== group.id ||
    eligibility.checkFailed !== false ||
    eligibility.eligibilityStatus !== "active" ||
    !isSameDayEligibilityCheck(eligibility.createdAt, group.timezone, now)
  ) {
    return "A current, active eligibility check for this patient is required before adding insurance. Verify coverage manually if electronic eligibility is unavailable.";
  }

  const request = eligibilityRequestSchema.safeParse(eligibility.requestJson);
  const fields = eligibilitySubscriberFieldsFromCoverage(coverage, patient);
  const customerPayer = resolveTprEligibilityCustomerPayer(coverage);
  const payerId = payers.find((payer) => payer.primaryPayerId === customerPayer)?.primaryPayerId;
  const subscriberDob = fields.checkAsDependent ? fields.subscriberDateOfBirth : fields.dateOfBirth;
  const today = currentTime.toFormat("yyyyMMdd");

  if (
    !request.success ||
    !payerId ||
    request.data.tradingPartnerServiceId !== payerId ||
    request.data.subscriber.memberId !== eligibilityRequestMemberId(payerId, coverage.subscriberId?.trim() ?? "") ||
    request.data.subscriber.firstName !== fields.subscriberGivenName?.trim() ||
    request.data.subscriber.lastName !== fields.subscriberFamilyName?.trim() ||
    request.data.subscriber.dateOfBirth !== subscriberDob?.replaceAll("-", "") ||
    !request.data.encounter.serviceTypeCodes.includes("30") ||
    [
      request.data.encounter.dateOfService,
      request.data.encounter.beginningDateOfService,
      request.data.encounter.endDateOfService,
    ].some((date) => date !== undefined && date !== today) ||
    (fields.checkAsDependent
      ? request.data.dependents?.[0]?.firstName !== patient.givenName ||
        request.data.dependents?.[0]?.lastName !== patient.familyName ||
        request.data.dependents?.[0]?.dateOfBirth !== patient.dateOfBirth?.replaceAll("-", "")
      : Boolean(request.data.dependents?.length))
  ) {
    return "The eligibility request does not match the coverage being added. Recheck the correct member and payer or verify coverage manually.";
  }

  const eligibilityProductText = [eligibility.planType, eligibility.planName].filter(Boolean).join(" ");
  const catalogPackageId = resolveConfirmedAthenaInsurancePlan(
    confirmedPlans,
    coverage,
    eligibility.payerName,
    eligibilityProductText,
    eligibility.planName
  )?.packageId;
  // Match the write boundary: canonical affiliations win, and a missing
  // affiliation must not fall back to the practice grid.
  const packageId = coverage.valerieInsurancePlanId
    ? catalogPackageId
    : (findUniqueTprInsurancePackageNumber({
        payer: coverage.payer,
        plan: coverage.plan,
        groupNumber: coverage.groupNumber,
        eligibilityProductText,
      }) ?? catalogPackageId);

  return packageId === insurancePackageId
    ? null
    : "Eligibility does not establish this Athena package match. Confirm the specific plan and network manually before adding coverage.";
}

export const tprAthenaCoverageWrite = {
  review: reviewTprCoverageWrite,
  memberId: normalizeTprMemberIdForAthenaWrite,
} satisfies AthenaCoverageWriteConfig;
